/**
 * ColoringQuiz Adult Renderer
 * Clean grid of toggle squares — no images, no Lottie, no student animations.
 * Uses the useColoringQuiz headless hook for all logic.
 */

import { useMemo } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText, QuizResultFeedback, SubmitButton, QuizSolveDescription, QuizMedia, QuizTypeBadge } from '../shared'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import type { ColoringQuizData } from '../hooks'
import { formatText } from '../../../utils/quiz-parser'
import { useColoringQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface ColoringQuizProps {
  quizId: string | number
  quiz: ColoringQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

export function ColoringAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: ColoringQuizProps) {
  const { t } = useTranslation('quiz')

  const {
    answers,
    isSolved,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    digits,
    inverted,
    hasImages,
    correctColorCount,
    itemsPerRow,
    rowIndices,
    coloredCount,
    handleToggle,
    handleSubmit,
    getImageSrc,
    getIsColored,
  } = useColoringQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // Feedback text
  const feedbackText = useMemo(() => {
    if (!isSolved) return ''

    if (shouldShowCorrectAnswers && correctColorCount !== null) {
      const count = inverted ? (digits - correctColorCount) : correctColorCount
      return inverted
        ? t('types.coloring.correctAnswerErase', { count })
        : t('types.coloring.correctAnswerColor', { count })
    }

    const count = inverted
      ? answers.filter(a => a === 0).length
      : coloredCount
    return inverted
      ? t('types.coloring.erased', { count })
      : t('types.coloring.colored', { count })
  }, [isSolved, shouldShowCorrectAnswers, correctColorCount, inverted, digits, answers, coloredCount, t])

  return (
    <div className="space-y-6">
      {/* Quiz type badge */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <QuizTypeBadge
          quizId={quizId}
          typeLabel={t('types.coloring.label')}
          difficultyLevel={difficultyLevel}
        />
      </motion.div>

      {/* Title + problem + media */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          'p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50',
          'border border-gray-100 shadow-2xs',
          'text-2xl font-medium leading-relaxed text-gray-900'
        )}
      >
        <KatexText text={quiz.title} />
        {quiz.problem && (() => {
          const cleanProblem = quiz.problem.replace(/\{\{\}\}/g, '').trim()
          if (!cleanProblem) return null
          return (
            <div className="mt-4 text-xl">
              {formatText(cleanProblem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
            </div>
          )
        })()}
        <QuizMedia image={quiz.image} imageMobile={quiz.imageMobile} desmos={quiz.desmos} vars={quiz.vars} />
      </motion.div>

      {/* Toggle grid */}
      <div className="flex flex-col items-center gap-2 py-4">
        {rowIndices.map((rowItems, rowIndex) => (
          <div key={rowIndex} className="flex items-center justify-center gap-2">
            {rowItems.map((itemIndex) => {
              const isColored = getIsColored(itemIndex)

              return (
                <motion.button
                  key={itemIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: itemIndex * 0.02 }}
                  onClick={() => handleToggle(itemIndex)}
                  disabled={isSolved || isViewSolved}
                  className={cn(
                    'relative cursor-pointer select-none',
                    (isSolved || isViewSolved) && 'pointer-events-none'
                  )}
                  aria-label={isColored ? t('types.coloring.colored', { count: 1 }) : ''}
                >
                  {hasImages ? (
                    <img
                      src={getImageSrc(itemIndex)}
                      alt={`item-${itemIndex}`}
                      className="w-auto h-auto hover:opacity-50 transition-opacity"
                      style={{ maxWidth: `${Math.min(80, Math.floor(800 / itemsPerRow))}px` }}
                      draggable={false}
                    />
                  ) : (
                    <div className={cn(
                      'w-14 h-14 rounded-lg border-2 transition-all duration-150',
                      isColored
                        ? 'bg-blue-500 border-blue-600 shadow-2xs'
                        : 'bg-gray-50 border-gray-300',
                      !isSolved && !isViewSolved && 'hover:border-blue-400 hover:shadow-md',
                    )} />
                  )}
                </motion.button>
              )
            })}
          </div>
        ))}
      </div>

      {/* Colored count feedback (after solve) */}
      {isSolved && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={cn(
            'rounded-xl text-base py-3 px-6 font-medium text-center',
            shouldShowCorrectAnswers || solveResult?.correct
              ? 'bg-green-50 text-green-600'
              : 'bg-red-50 text-red-500'
          )}
        >
          {feedbackText}
        </motion.div>
      )}

      {/* Submit button */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={false}
          label={submitLabel}
        />
      )}

      {/* Result feedback */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          correctAnswer={shouldShowCorrectAnswers && correctColorCount !== null
            ? (inverted
                ? t('types.coloring.eraseCount', { count: digits - correctColorCount })
                : t('types.coloring.colorCount', { count: correctColorCount }))
            : undefined}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* Solve description */}
      {isSolved && solveResult?.description && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult.description}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}
